import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  STORE_NEAR_YOU,
  USING_SANDBOX_ENVIRONMENT,
  BASE_URL_FOR_ELASTIC_SEARCH,
} from '../../../Constants/Config';
import { waitForElasticConnection } from '../../../hooks/checkElasticConnection.js';
import { createRequestCache } from '../../../utils/requestCache';

const nearbyStoresRequestCache = createRequestCache({
  ttlMs: 45 * 1000,
  maxEntries: 40,
});

const initialState = {
  loading: false,
  shopBYStoreList: [],
  error: '',
  totalPages: 0,
  hasMore: true,
  activeRequestId: null,
};

export const fetchShopByStoreLists = createAsyncThunk(
  'shopBYStoreList/fetchShopByStoreLists',
  async ({ payload = {}, signal } = {}, { rejectWithValue, getState }) => {
    try {
      const requestLat = payload?.lat ?? '';
      const requestLong = payload?.long ?? '';
      const requestStore = payload?.store || '';
      const requestKey = JSON.stringify([
        BASE_URL_FOR_ELASTIC_SEARCH,
        BASE_URL_SANDBOX,
        STORE_NEAR_YOU,
        String(requestLat),
        String(requestLong),
        requestStore,
        String(USING_SANDBOX_ENVIRONMENT),
      ]);

      return await nearbyStoresRequestCache.getOrCreate(
        requestKey,
        async () => {
          const isElasticConnected = await waitForElasticConnection(getState);
          const baseUrl = isElasticConnected
            ? BASE_URL_FOR_ELASTIC_SEARCH
            : BASE_URL_SANDBOX;
          const formData = new FormData();
          formData.append('lat', requestLat);
          formData.append('long', requestLong);
          formData.append('store', requestStore);

          const response = await axios.post(
            `${baseUrl}${STORE_NEAR_YOU}`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          );

          if (response.status === 200 && response.data && response.data.data) {
            const shopBYStoreList = response.data.data.filter(
              ({ flag }) => +flag === USING_SANDBOX_ENVIRONMENT,
            );
            return {
              shopBYStoreList,
              totalPages: response?.data?.totalPages,
            };
          }

          return { shopBYStoreList: [], totalPages: 0, hasMore: false };
        },
        {
          forceRefresh: Boolean(payload?.forceRefresh),
          signal,
        },
      );
    } catch (e) {
      return rejectWithValue(e.message);
    }
  },
);

const shopByStoreSlice = createSlice({
  name: 'shopBYStoreList',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchShopByStoreLists.pending, (state, action) => {
        state.loading = true;
        state.activeRequestId = action.meta.requestId;
        state.shopBYStoreList = [];
      })
      .addCase(fetchShopByStoreLists.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.activeRequestId = null;
        state.shopBYStoreList = action.payload?.shopBYStoreList;
        state.totalPages = action.payload?.totalPages || 0;
        state.error = '';
      })
      .addCase(fetchShopByStoreLists.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.activeRequestId = null;
        if (!action.meta.aborted) {
          state.error = action.payload || 'Shop by Stores Near You';
        }
        state.hasMore = false;
      });
  },
});

export default shopByStoreSlice.reducer;
